import React from 'react';
import {t} from '@lingui/core/macro';
import {Trans} from '@lingui/react';

import MainModal from 'shared/ui/Modal/MainModal';
import {ExceededModal} from 'entities/user';
import LabelWithHint from 'shared/ui/FormGroup/LabelWithHint/LabelWithHint';
import {MonitorToggle, useMonitorModalState} from 'entities/monitor';
import useOpenState from 'shared/lib/hooks/useOpenState';
import useCanCreateDomainMonitor from 'features/domain/model/useCanCreateDomainMonitor';
import IDomainMonitorOutput from 'features/domain/model/DomainMonitor/IDomainMonitor';

import DomainMonitorForm from './DomainMonitorForm/DomainMonitorForm';
import DomainMonitorBatchCreateForm from './DomainMonitorForm/DomainMonitorBatchCreateForm';

interface IDomainMonitorModalProps {
    open: boolean,
    item?: IDomainMonitorOutput,
    handleClose: () => void,
}

const DomainMonitorModal: React.FC<IDomainMonitorModalProps> = ({open, item, handleClose}) => {
    const exceeded = useOpenState();
    const canCreate = useCanCreateDomainMonitor();

    const isNew = !item;
    const {isBatchMode, showToggle, toggleBatchMode, setShowResult, handleCloseAndReset} = useMonitorModalState(
        isNew,
        handleClose,
    );
    // eslint-disable-next-line max-len
    const batchHint = t`Каждый домен с новой строки. Можно указывать домен, адрес с www или полную ссылку — лишнее отбросится автоматически.`;
    const domainHint = isBatchMode
        // eslint-disable-next-line max-len
        ? t`Можно указывать домен, адрес с www или полную ссылку — лишнее отбросится автоматически. Дубликаты и ошибочные домены попадут в отчёт.`
        // eslint-disable-next-line max-len
        : t`Можно указать домен, адрес с www или полную ссылку — лишнее отбросится автоматически. После создания домен изменить нельзя.`;

    const getHeader = () => {
        if (isBatchMode) {
            return t`Создание мониторов доменов`;
        }
        return isNew ? t`Создание монитора домена` : t`Редактирование монитора домена`;
    };

    return (
        <>
            <MainModal
                header={getHeader()}
                open={open}
                handleClose={handleCloseAndReset}
                size="md"
            >
                {showToggle && (
                    <MonitorToggle
                        isBatchMode={isBatchMode}
                        handleModeToggle={toggleBatchMode}
                        label={
                            <LabelWithHint
                                label={<Trans id="add.domainMonitor.modal.toggle.title" message="Домен"/>}
                                hint={domainHint}
                            />
                        }
                        hint={batchHint}
                    />
                )}
                {isBatchMode ? (
                    canCreate ? (
                        <DomainMonitorBatchCreateForm
                            handleClose={handleCloseAndReset}
                            onShowResultChange={setShowResult}
                        />
                    ) : (
                        <ExceededModal isOpen={true} handleClose={handleCloseAndReset} type="domain_monitors"/>
                    )
                ) : (
                    <DomainMonitorForm
                        item={item}
                        onSuccess={handleCloseAndReset}
                        onLimitExceeded={exceeded.open}
                        onClose={handleCloseAndReset}
                    />
                )}
            </MainModal>
            <ExceededModal
                isOpen={exceeded.isOpen}
                handleClose={exceeded.close}
                type="domain_monitors"
            />
        </>
    );
};

export default DomainMonitorModal;
